import { useEffect } from 'react';
import { Link, Navigate, Outlet, useLocation, useNavigate } from 'react-router';
import { Logo } from '../../shared/ui/components';
import { useMe, useSession } from '../session/SessionProvider';
import { useAppUpdate } from '../presentation/appUpdate';
import { inDesktopApp } from '../presentation/screens';
import { Icon, isActive, type NavItem } from './nav';

/** Configuración de la app (engrane): monitores y tema de las pantallas. */
const CONFIG: NavItem = { to: '/app/configuracion', label: 'Configuración', short: 'Configuración', icon: 'gear' };

/** Lo único que se usa en el equipo de multimedia. */
const ITEMS: NavItem[] = [
  { to: '/app/presentaciones', label: 'Cultos', short: 'Cultos', icon: 'screen' },
  { to: '/app/cantos', label: 'Cantos', short: 'Cantos', icon: 'music' },
  { to: '/app/multimedia', label: 'Multimedia', short: 'Multimedia', icon: 'image' },
  { to: '/app/biblia', label: 'Biblia', short: 'Biblia', icon: 'bible' },
];

/**
 * Shell del equipo de multimedia: la computadora de proyección vinculada a la iglesia. No es la
 * cuenta de nadie, así que no hay avisos, perfil ni menú personal; «Desvincular» la suelta.
 */
export function DeviceLayout() {
  const me = useMe();
  const { logout } = useSession();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const update = useAppUpdate();
  const allowed = ITEMS.some((i) => isActive(i, pathname)) || pathname.startsWith('/app/pantalla') || pathname === CONFIG.to;

  useEffect(() => {
    document.documentElement.classList.add('is-device');
    return () => document.documentElement.classList.remove('is-device');
  }, []);

  async function unlink() {
    if (!window.confirm('¿Desvincular esta computadora? Para volver a usarla como equipo de multimedia alguien tendrá que entrar con su cuenta y vincularla otra vez.')) return;
    await logout();
    navigate('/', { replace: true });
  }

  // Inicio y lo personal no existen aquí: todo lleva a Presentaciones.
  if (!allowed) return <Navigate to="/app/presentaciones" replace />;

  const link = (item: NavItem, className: string, label: string) => {
    const active = isActive(item, pathname);
    return (
      <Link key={item.to} to={item.to} title={item.label} className={`${className}${active ? ' active' : ''}`} aria-current={active ? 'page' : undefined}>
        <Icon name={item.icon} />
        <span>{label}</span>
      </Link>
    );
  };

  return (
    <div className="shell shell--device">
      <a href="#contenido" className="skip-link">
        Saltar al contenido
      </a>
      <main id="contenido" className="content">
        <Outlet />
      </main>
      {/* Una cinta abajo en lugar del menú lateral y sin franja arriba: deja todo el espacio para la consola. */}
      <nav className="bottomnav devicebar" aria-label="Principal">
        {/* Sin franja de arriba: el logo y la iglesia van aquí para dejarle todo el alto a la consola. */}
        <span className="devicebar__brand" title={`Equipo de multimedia · ${me.activeContext?.churchName ?? ''}`}>
          <Logo />
          <span className="devicebar__church">{me.activeContext?.churchName ?? ''}</span>
        </span>
        {ITEMS.map((item) => link(item, 'bottomnav__item', item.label))}
        <span className="devicebar__spacer" aria-hidden="true" />
        {update.available ? (
          <Link to={CONFIG.to} className="bottomnav__item devicebar__update" title={`Hay una versión nueva: ${update.latest}`}>
            <Icon name="bolt" />
            <span>Actualizar</span>
          </Link>
        ) : null}
        {inDesktopApp() ? link(CONFIG, 'bottomnav__item', CONFIG.label) : null}
        <button type="button" className="bottomnav__item devicebar__button" onClick={() => void unlink()} title="Desvincular esta computadora">
          <Icon name="logout" />
          <span>Desvincular</span>
        </button>
      </nav>
    </div>
  );
}
